/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { CustomReport, CustomReportsValue, MetricType } from '@g17eco/types/custom-report';
import { UniversalTrackerValueView } from '@features/universal-tracker-value';
import UniversalTracker from '../../model/UniversalTracker';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { CalculationVariable } from '../../types/universalTracker';
import { useAppSelector } from '../../reducers';
import './styles.scss';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Table } from '@g17eco/molecules/table';
import { DisaggregationUtrv } from '../../types/universalTrackerValue';

interface CustomReportsMetricDetailsModalProps {
  customReport: CustomReport;
  utrId: string;
  metricValues: CustomReportsValue[];
  toggle: () => void;
}

const getFormattedValue = (reportValue?: CustomReportsValue) => {
  if (!reportValue) {
    return <>-</>;
  }
  if (reportValue.customMetricType === MetricType.Text) {
    return <SimpleTooltip text='Can only be viewed in file download'>
      <i className='fa fa-info-circle' />
    </SimpleTooltip>;
  }
  const universalTracker = new UniversalTracker(reportValue.utr);
  return <UniversalTrackerValueView
    utr={universalTracker}
    utrv={reportValue.utrv}
    valueType={universalTracker.getValueType()}
  />
}

const getAggregationMethodName = (method: string) => {
  switch (method) {
    case 'valueCountAggregator':
      return 'Count';
    default:
      return 'Default';
  }
}

const CustomReportsMetricDetailsModal = (props: CustomReportsMetricDetailsModalProps) => {
  const { utrId, toggle, customReport, metricValues } = props;

  const getUtrName = (utrCode: string | undefined) => {
    if (!utrCode) {
      return '-';
    }
    return metricValues?.find(({ utr }) => utr.code === utrCode)?.utr.name ?? '-';
  }

  const utr = customReport.universalTrackers?.find(utr => utr._id === utrId);
  const value = metricValues.find(({ utr }) => utr._id === utrId);

  if (!utr) {
    return <></>;
  }

  const variableKeys = utr.calculation?.variables ? Object.keys(utr.calculation?.variables) : [];

  const variables = variableKeys.map((v: string) => ({
    ...utr.calculation?.variables[v],
    variable: v,
    utrName: getUtrName(utr.calculation?.variables[v].code)
  }));

  return (
    <Modal isOpen={true} toggle={toggle} backdrop='static' size='lg'>
      <ModalHeader toggle={toggle}>{utr.name}</ModalHeader>
      <ModalBody>
        <h2>
          {(value?.utrv.aggregationCount ?? 0) > 0 ? 'Aggregated Result' : 'Result'}: {getFormattedValue(value)}
        </h2>
        {utr.calculation?.formula ? <h2>Calculation: {utr.calculation?.formula}</h2> : <></>}
        <Table
          columns={[
            {
              header: '',
              accessorKey: 'variable',
            },
            {
              header: 'Metric',
              accessorKey: 'utrName',
            },
            {
              header: 'Aggregation Method',
              accessorFn: (row) => getAggregationMethodName(row.valueAggregation ?? ''),
            },
            {
              header: 'Lookup Filter',
              accessorKey: 'valueListCode',
            },
          ]}
          data={variables}
        />
        {variableKeys.map((variableKey) => {
          const v = utr.calculation?.variables[variableKey];
          if (!v) {
            return null;
          }
          return (
            <CalculationMetricDetails
              key={variableKey}
              variableKey={variableKey}
              variableDetails={v}
              metricValues={metricValues}
            />
          );
        })}
      </ModalBody>
    </Modal>
  );
}

interface VariableDetailsProps {
  variableKey: string;
  metricValues: CustomReportsValue[];
  variableDetails: CalculationVariable;
}

const CalculationMetricDetails = (props: VariableDetailsProps) => {
  const { variableKey, variableDetails, metricValues } = props;

  const initiativeTree = useAppSelector((state) => state.globalData.loaded ? state.globalData.data.initiativeTree : []);

  const getInitiativeName = (initiativeId: string) => {
    const initiative = initiativeTree.find(i => i._id === initiativeId);
    if (initiative) {
      return initiative.name;
    }
    return initiativeId;
  }

  const data = metricValues.find(({ utr }) => utr.code === variableDetails.code);
  const columnCode = variableDetails.valueListCode;

  if (!data) {
    return null;
  }

  const rows = data.utrv.disaggregation?.map(disaggregation => ({
    name: disaggregation.initiativeId,
    lookupValue: getValue(disaggregation, columnCode, variableDetails.valueAggregation)
  })) ?? [
      {
        name: 'Current Level',
        lookupValue: getValue(data.utrv, columnCode, variableDetails.valueAggregation)
      }
    ]

  const sum = rows.reduce((acc, row) => acc + Number(row.lookupValue), 0);

  return (
    <>
      <hr />
      <h3 className='mt-3 strong'>
        {variableKey}) {data.utr.name}
      </h3>
      {data.utr.calculation?.formula ? (
        <h4>Calculation: {data.utr.calculation?.formula === '{a}' ? 'Sum' : data.utr.calculation?.formula}</h4>
      ) : (
        <></>
      )}
      {variableDetails.valueAggregation ? (
        <h4>Aggregation Method: {getAggregationMethodName(variableDetails.valueAggregation)}</h4>
      ) : (
        <></>
      )}
      {variableDetails.valueListCode ? <h4>Lookup Filter: {variableDetails.valueListCode}</h4> : <></>}
      <Table
        pageSize={10}
        className='mt-3'
        columns={[
          {
            header: 'Reporting Level',
            accessorFn: (row) => getInitiativeName(row.name),
          },
          {
            id: 'lookupValue',
            header: 'Result',
            meta: {
              cellProps: {
                className: 'text-right',
              },
            },
            cell: ({ row }) => <span className='pr-4'>{row.original.lookupValue}</span>,
          },
        ]}
        data={rows}
      />
      <h4 className='pr-4 text-right'>Total: {sum}</h4>
    </>
  );
}

const getValue = (utrv: DisaggregationUtrv, columnCode?: string, valueAggregation?: string | null) => {
  if (valueAggregation === 'valueCountAggregator') {
    return 1;
  }
  if (!columnCode) {
    return utrv.value;
  }
  if ('valueData' in utrv) {
    const values: (string | number)[] = [];
    const data = utrv.valueData?.table?.reduce((acc, row) => {
      const column = row.find(col => col.code === columnCode);
      if (column && column.value !== undefined) {
        if (Array.isArray(column.value)) {
          column.value.forEach(v => acc.push(v));
        } else {
          acc.push(column.value);
        }
      }
      return acc;
    }, values);
    return data?.join(', ');
  }
  return '';
}

export default CustomReportsMetricDetailsModal;
